import axios from "axios";
import { useState, useContext } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import buttonStyle from "../../styles/button-style.module.scss";
import style from "../../styles/modal.module.scss";
import { getServerURL } from "../../utils/funcs/get-serverURL";
import { PaymentContext } from "../../context/payment-success-context";
import { trackEvent } from "../../analytics/events";
import { addVAT, formatCurrency } from "../../utils/funcs/pricing";

function SummaryModal({
  show,
  handleClose,
  item,
  setError,
  serviceRequired,
}: any) {
  const [loading, setLoading] = useState(false);
  const { setBookingItem } = useContext(PaymentContext);

  const serverURL = getServerURL();

  const getEmailEndTime = (startTime?: string): string => {
    if (!startTime) return "";

    const normalized = startTime.trim().toUpperCase();

    const timeMap: { [key: string]: string } = {
      "08:00 AM": "1:00 PM",
      "8:00 AM": "1:00 PM",
      "1:00 PM": "6:00 PM",
      "13:00": "18:00",
    };

    return timeMap[normalized] || "";
  };

  const resolvedEmailEndTime =
    item?.emailEndTime || getEmailEndTime(item?.emailStartTime);

  const checkoutItem = {
    ...item,
    finalPrice: addVAT(item?.finalPrice ?? item?.price),
    bookingID: item?.bookingID || Date.now(),
    emailEndTime: resolvedEmailEndTime,
    address: {
      ...(item?.address || {}),
    },
  };

  const toLocalISOString = (val: any): string => {
    const d = new Date(val);
    const pad = (n: number) => String(n).padStart(2, "0");

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate(),
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const normalizedCheckoutItem = {
    ...checkoutItem,
    bookingID: String(checkoutItem.bookingID || ""),
    finalPrice: Number(checkoutItem.finalPrice || 0),

    date: checkoutItem.date
      ? new Date(checkoutItem.date).toISOString()
      : "",

    start: checkoutItem.start
      ? toLocalISOString(checkoutItem.start)
      : "",

    end: checkoutItem.end
      ? toLocalISOString(checkoutItem.end)
      : "",

    emailDate: checkoutItem.emailDate
      ? String(checkoutItem.emailDate)
      : "",

    emailStartTime: checkoutItem.emailStartTime
      ? String(checkoutItem.emailStartTime)
      : "",

    emailEndTime: checkoutItem.emailEndTime
      ? String(checkoutItem.emailEndTime)
      : "",

    address: {
      line1: checkoutItem?.address?.line1 || "",
      line2: checkoutItem?.address?.line2 || "",
      postTown: checkoutItem?.address?.postTown || "",
      postcode: checkoutItem?.address?.postcode || "",
    },
  };

  const hasRequiredCheckoutFields =
    Boolean(normalizedCheckoutItem?.productName) &&
    Boolean(normalizedCheckoutItem?.customerName) &&
    Boolean(normalizedCheckoutItem?.customerEmail) &&
    Boolean(normalizedCheckoutItem?.emailDate) &&
    Boolean(normalizedCheckoutItem?.emailStartTime) &&
    Boolean(normalizedCheckoutItem?.address?.line1) &&
    Boolean(normalizedCheckoutItem?.address?.postcode) &&
    Boolean(normalizedCheckoutItem?.finalPrice);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    trackEvent("proceed_to_payment_click", {
      page: window.location.pathname,
      service: normalizedCheckoutItem.productName,
      service_name: normalizedCheckoutItem.productName,
      product_name: normalizedCheckoutItem.productName,
    });

    if (!hasRequiredCheckoutFields) {
      setError(
        "Your booking details are incomplete. Please go back and try again.",
      );
      return;
    }

    setLoading(true);
    setError("");

    setBookingItem(normalizedCheckoutItem);

    localStorage.setItem("item", JSON.stringify(normalizedCheckoutItem));
    localStorage.setItem("emailSent", "false");
    localStorage.setItem("bookingCreated", "false");

    if (serviceRequired === "Full Rewiring") {
      try {
        const res = await axios.post(`${serverURL}/book-free-quotation`, {
          item: normalizedCheckoutItem,
        });

        setLoading(false);

        if (res) {
          window.location.href = "/booking-success";
        }
      } catch (err: any) {
        setLoading(false);

        console.error(
          "Free quotation error details:",
          err.response?.data || err.message,
        );

        setError("An error occurred while booking. Please try again.");
      }
    } else {
      try {
        const res = await axios.post(
          `${serverURL}/create-checkout-session`,
          {
            item: normalizedCheckoutItem,
          },
        );

        setLoading(false);

        if (res.data?.url) {
          window.location.href = res.data.url;
        } else {
          setError(
            "Unable to start Stripe checkout. Please try again.",
          );
        }
      } catch (err: any) {
        setLoading(false);

        console.error(
          "Stripe checkout error details:",
          err.response?.data || err.message,
        );

        setError(
          "An error occurred while processing payment. Please try again.",
        );
      }
    }
  };

  const fullAddress = [
    checkoutItem?.address?.line1,
    checkoutItem?.address?.line2?.trim(),
    checkoutItem?.address?.postTown,
    checkoutItem?.address?.postcode,
  ]
    .filter(Boolean)
    .join(", ");

  const appointmentTime = checkoutItem.emailEndTime
    ? `${checkoutItem.emailStartTime} to ${checkoutItem.emailEndTime}`
    : checkoutItem.emailStartTime;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName={style.summaryDialog}
      contentClassName={style.summaryModal}
    >
      <Modal.Header
        closeButton
        className={style.modalHeader}
      >
        <div>
          <Modal.Title className={style.modalTitle}>
            Booking summary
          </Modal.Title>

          <p className={style.modalSubtitle}>
            Check everything looks right before payment.
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className={style.body}>
        <div className={style.summarySections}>
          {/* SERVICE */}
          <section className={style.summarySection}>
            <div className={style.sectionHeader}>
              <span className={style.sectionLabel}>
                Service
              </span>

              <span className={style.price}>
                {formatCurrency(checkoutItem.finalPrice)}
              </span>
            </div>

            <div className={style.detailRows}>
              <div className={style.detailRow}>
                <span>Service</span>
                <strong>{checkoutItem.productName}</strong>
              </div>

              <div className={style.detailRow}>
                <span>Property</span>
                <strong>{checkoutItem.productInfo}</strong>
              </div>
            </div>
          </section>

          {/* CUSTOMER */}
          <section className={style.summarySection}>
            <div className={style.sectionHeader}>
              <span className={style.sectionLabel}>
                Customer
              </span>
            </div>

            <div className={style.detailRows}>
              <div className={style.detailRow}>
                <span>Name</span>
                <strong>{checkoutItem.customerName}</strong>
              </div>

              <div className={style.detailRow}>
                <span>Email</span>
                <strong>{checkoutItem.customerEmail}</strong>
              </div>

              <div className={style.detailRow}>
                <span>Phone</span>
                <strong>{checkoutItem.customerNumber}</strong>
              </div>
            </div>
          </section>

          {/* APPOINTMENT */}
          <section
            className={`${style.summarySection} ${style.appointmentSection}`}
          >
            <div className={style.sectionHeader}>
              <span className={style.sectionLabel}>
                Appointment
              </span>
            </div>

            <div className={style.appointmentGrid}>
              <div className={style.appointmentItem}>
                <span>Date</span>
                <strong>{checkoutItem.emailDate}</strong>
              </div>

              <div className={style.appointmentItem}>
                <span>Time</span>
                <strong>{appointmentTime}</strong>
              </div>

              <div
                className={`${style.appointmentItem} ${style.addressItem}`}
              >
                <span>Address</span>
                <strong>{fullAddress}</strong>
              </div>

              {checkoutItem.access && (
                <div
                  className={`${style.appointmentItem} ${style.accessItem}`}
                >
                  <span>Access</span>
                  <strong>{checkoutItem.access}</strong>
                </div>
              )}
            </div>
          </section>
        </div>
      </Modal.Body>

      <Modal.Footer className={style.modalFooter}>
        <p className={style.redirect}>
          By clicking{" "}
          <strong>
            {serviceRequired === "Full Rewiring"
              ? "Book now"
                : `Pay ${formatCurrency(checkoutItem.finalPrice)}`}
          </strong>
          , you confirm the details above are correct and accept our{" "}
          <a
            href="/terms&conditions"
            target="_blank"
            rel="noreferrer"
          >
            terms and conditions
          </a>
          .
        </p>

        <div className={style.actionRow}>
          {loading ? (
            <div
              className={style.loadingBtn}
              aria-label="Loading"
            >
              <span />
            </div>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              type="submit"
              className={`${buttonStyle.modalBtn} ${style.payButton}`}
            >
              {serviceRequired === "Full Rewiring"
                ? "Book now"
                : `Pay ${formatCurrency(checkoutItem.finalPrice)}`}
              <span aria-hidden="true">→</span>
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default SummaryModal;